import React, { useEffect, useMemo, useState } from "react";
import { Block } from "@randajan/jet-react/dom/block";
import { Alert, Box, Button, FormControl, FormControlLabel, FormHelperText, Switch, Typography } from "@mui/material";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";

import { validateProfileForm } from "../../../../../arc/forms/profileForms.js";
import { Say } from "../../../../../arc/locale/consts.js";
import { meBeam } from "../../../../assets/beams/meBeam";
import { BlimpMuiFormActions } from "../../../../elements/BlimpMuiFormActions/BlimpMuiFormActions";
import { BlimpMuiFormSection } from "../../../../elements/BlimpMuiFormSection/BlimpMuiFormSection";
import { useBeforeUnload } from "../../../../assets/hooks/useBeforeUnload.jsx";
import { getProfileErrorId, getValidationFieldError, handleProfileSubmitReply, mergeValidation } from "../profileValidation.js";
import { mailMeFields } from "./mailMeConfig";
import { createMailMeValues, formatMuteDaysLeft, getActiveMuteTill, getMuteFieldName, mailMeFieldNames } from "./mailMeFormTools";

import "./MailMeForm.scss";
import { Locale } from "../../../../assets/Locale.js";

const emptyValues = Object.freeze({});

const touchAllFields = _=>Object.fromEntries(mailMeFieldNames.map(field=>[field, true]));

const MailMeFormOption = (props)=>{
    const { field, fieldError, isMuted, muteLabel, onChange, onClearMute, showError, value } = props;
    const say = Locale.use();
    const className = isMuted
        ? "BlimpMuiForm-toggle isMuted"
        : value ? "BlimpMuiForm-toggle isChecked" : "BlimpMuiForm-toggle";
    const stateId = isMuted ? "muted" : value ? "on" : "off";
    const fieldId = (...parts)=>Say.id("profile", "mailMe", "field", field.name, ...parts);

    return (
        <FormControl className={className} error={showError}>
            <FormControlLabel
                className="BlimpMuiForm-toggleControl"
                control={<Switch checked={value} name={field.name} onChange={onChange}/>}
                label={(
                    <Box className="BlimpMuiForm-toggleLabelWrap">
                        <Typography className="BlimpMuiForm-toggleLabel">{say(fieldId("label"))}</Typography>
                        <Typography className="BlimpMuiForm-toggleState">
                            {say(Say.id("profile", "mailMe", "state", stateId))}
                        </Typography>
                    </Box>
                )}
            />
            <Typography className="BlimpMuiForm-toggleText">
                {say(fieldId("description"))}
            </Typography>
            {isMuted && (
                <Box className="MailMeForm-mute">
                    <Typography className="MailMeForm-muteText">
                        {say("profileMailMeMuteEndsIn").replace("$", muteLabel)}
                    </Typography>
                    <Button
                        className="MailMeForm-muteButton"
                        onClick={onClearMute}
                        size="small"
                        startIcon={<NotificationsActiveOutlinedIcon />}
                        type="button"
                        variant="outlined"
                    >
                        {say("profileMailMeMuteClear")}
                    </Button>
                </Box>
            )}
            {showError && (
                <FormHelperText>{say(getProfileErrorId(field.name, fieldError))}</FormHelperText>
            )}
        </FormControl>
    );
};

export const MailMeForm = (props)=>{
    const { profile, onSaved, onCancel } = props;
    const say = Locale.use();
    const initialValues = useMemo(()=>createMailMeValues(profile), [profile]);
    const [ values, setValues ] = useState(initialValues);
    const [ touched, setTouched ] = useState(emptyValues);
    const [ submitState, setSubmitState ] = useState("");
    const [ serverValidation, setServerValidation ] = useState(null);
    const [ isSubmitting, setIsSubmitting ] = useState(false);

    useEffect(()=>{
        setValues(initialValues);
        setTouched(emptyValues);
        setServerValidation(null);
        setSubmitState("");
    }, [initialValues]);

    useBeforeUnload();

    const localValidation = useMemo(()=>validateProfileForm("mailMe", values), [values]);
    const validation = useMemo(()=>mergeValidation(localValidation, serverValidation), [localValidation, serverValidation]);
    const showAllErrors = submitState === "validationError";

    const setFieldValue = event=>{
        const { checked, name } = event.target;
        const muteFieldName = getMuteFieldName(name);
        setValues(current=>({ ...current, [name]:checked, [muteFieldName]:checked ? current[muteFieldName] : null }));
        setTouched(current=>({ ...current, [name]:true, [muteFieldName]:true }));
        setServerValidation(null);
        setSubmitState("");
    };

    const clearMute = (event, fieldName)=>{
        event.preventDefault();
        const muteFieldName = getMuteFieldName(fieldName);
        setValues(current=>({ ...current, [muteFieldName]:null }));
        setTouched(current=>({ ...current, [muteFieldName]:true }));
        setServerValidation(null);
        setSubmitState("");
    };

    const submit = async event=>{
        event.preventDefault();
        const result = validateProfileForm("mailMe", values);
        if (!result.isValid) {
            setServerValidation(null);
            setTouched(touchAllFields());
            setSubmitState("validationError");
            return;
        }
        setIsSubmitting(true);
        try {
            const reply = await meBeam.act.updateMailMe(result.values);
            handleProfileSubmitReply(reply, {
                fields:mailMeFieldNames,
                setIsSubmitting,
                setServerValidation,
                setSubmitState,
                setTouched,
                onDone:onSaved
            })
        } catch(err) {
            setSubmitState("submitError");
            setIsSubmitting(false);
        }
    };

    const now = Date.now();

    return (
        <Block level={0} className="MailMeForm popScreen" caption={say("profileMailMeFormCaption")}>
            <Box className="BlimpMuiForm" component="form" onSubmit={submit} noValidate>
                <Typography className="BlimpMuiForm-intro">
                    {say("profileMailMeIntro")}
                </Typography>
                {submitState === "validationError" && (
                    <Alert severity="error">{say("formErrorHighlightedSwitches")}</Alert>
                )}
                {submitState === "submitError" && (
                    <Alert severity="error">{say("profileMailMeErrorSubmit")}</Alert>
                )}
                <Box className="BlimpMuiForm-sections">
                    <BlimpMuiFormSection bodyClassName="BlimpMuiForm-toggleList" icon={TuneOutlinedIcon} title={say("profileMailMeSectionNotifications")}>
                        {mailMeFields.map(field=>{
                            const fieldError = getValidationFieldError(validation, field.name);
                            const muteTill = values[field.name] ? getActiveMuteTill(values[getMuteFieldName(field.name)], now) : null;
                            return (
                                <MailMeFormOption
                                    key={field.name}
                                    field={field}
                                    fieldError={fieldError}
                                    isMuted={!!muteTill}
                                    muteLabel={muteTill ? formatMuteDaysLeft(say, muteTill, now) : ""}
                                    onChange={setFieldValue}
                                    onClearMute={event=>clearMute(event, field.name)}
                                    showError={!!fieldError && (showAllErrors || touched[field.name])}
                                    value={values[field.name]}
                                />
                            );
                        })}
                    </BlimpMuiFormSection>
                </Box>
                <BlimpMuiFormActions
                    onCancel={profile.isNewbie ? null : onCancel}
                    submitDisabled={isSubmitting}
                    submitLabel={isSubmitting ? say("commonSaving") : say("commonSave")}
                />
            </Box>
        </Block>
    );
};
